import {
  addDays,
  countedBlocks,
  daysBetween,
  formatDuration,
  relativeDay,
  startOfWeek,
  type CalendarAllDayEvent,
  type CalendarBlock,
  type CalendarDueItem,
  type ISODate,
  type Task,
} from '@app/shared'

// ============================================================================
// The day rail's derivations — everything the right-hand column says about a day, computed in
// one place so the three cards cannot disagree with each other or with the grid.
//
// Two rules from docs/design/calendar-brief.md are enforced here rather than in a component:
//   · UNSCHEDULED MEANS UNSCHEDULED. A task with an accepted block linked to it is scheduled and
//     leaves the list; a task with only a SUGGESTED block is still unscheduled, because an
//     unaccepted suggestion is not on the calendar for any purpose (rule 2). `countedBlocks`
//     is what makes that true, and it is the same function the day summary counts with.
//   · "Empty rail state: 'Nothing unscheduled' — do not backfill with completed items."
// ============================================================================

/**
 * The open tasks with no time held for them, most urgent first.
 *
 * Order: overdue, then due soonest, then undated — the same ordering the Tasks board uses, so
 * the two screens agree about what is next. Nothing is ranked, scored or inferred from wording.
 */
export function unscheduledTasks(
  tasks: readonly Task[],
  blocks: readonly CalendarBlock[],
  today: ISODate,
): Task[] {
  const held = new Set(
    countedBlocks(blocks)
      .map((b) => b.taskId)
      .filter((id): id is string => id !== null),
  )
  return tasks
    .filter((t) => !t.done && !held.has(t.id))
    .sort((a, b) => {
      const aLate = a.due !== null && a.due < today
      const bLate = b.due !== null && b.due < today
      if (aLate !== bLate) return aLate ? -1 : 1
      if (a.due === b.due) return a.title < b.title ? -1 : 1
      if (a.due === null) return 1
      if (b.due === null) return -1
      return a.due < b.due ? -1 : 1
    })
}

export type UrgencyTone = 'due' | 'canvas' | 'neutral'

export interface TaskLine {
  /** 'due today · ~30m' · 'no due date · ~15m' · 'Sep 21'. Never a made-up length. */
  text: string
  tone: UrgencyTone
}

/**
 * The second line of an unscheduled row: what the task's date says, then its estimate if it has
 * one. A task with no estimate says nothing about length — the app does not read a duration out
 * of a title, which is the same rule that decides whether it can be suggested at all.
 */
export function taskLine(task: Task, today: ISODate): TaskLine {
  const when =
    task.due === null
      ? 'no due date'
      : task.due < today
        ? `overdue · ${relativeDay(task.due, today)}`
        : `due ${relativeDay(task.due, today)}`
  const tone: UrgencyTone =
    task.due === null ? 'neutral' : task.due <= today ? 'due' : 'canvas'
  const estimate = task.estimateMin === null ? null : `~${formatDuration(task.estimateMin)}`
  return { text: [when, estimate].filter(Boolean).join(' · '), tone }
}

/**
 * The nearest deadline that still needs doing, from today forward — the focus card's subject.
 *
 * Null is a real answer and the card says so in words. Submitted and graded work is excluded:
 * a deadline that has been met is not a deadline, and putting one on a focus card would be the
 * screen inventing pressure.
 */
export function nextDeadline(
  dueItems: readonly CalendarDueItem[],
  today: ISODate,
): CalendarDueItem | null {
  const open = dueItems
    .filter((d) => d.status === 'upcoming' || d.status === 'missing')
    .sort((a, b) =>
      a.date === b.date
        ? (a.dueMinutes ?? 0) - (b.dueMinutes ?? 0)
        : a.date < b.date
          ? -1
          : 1,
    )
  return open.find((d) => d.date >= today) ?? open[0] ?? null
}

/**
 * Minutes from now until a deadline; negative once it has passed.
 *
 * Null when Canvas gave no time — and that null is the whole point. A countdown needs a moment,
 * a date is not one, and filling in 11:59 PM to make the number appear is exactly what brief
 * rule 5 forbids. The card prints the day instead.
 */
export function minutesUntil(
  item: CalendarDueItem,
  today: ISODate,
  nowMinutes: number,
): number | null {
  if (item.dueMinutes === null) return null
  return daysBetween(today, item.date) * 1440 + (item.dueMinutes - nowMinutes)
}

/** 'in 13h 47m' / '14h ago'. Built from `formatDuration`, so one vocabulary for every length. */
export function countdownPhrase(minutes: number): string {
  return minutes >= 0 ? `in ${formatDuration(minutes)}` : `${formatDuration(-minutes)} ago`
}

// ── The month grid's derivations ────────────────────────────────────────────
//
// Both functions here are PURE and live outside the component for the usual reason on this
// screen: the month view's honesty rules (what a cell may show, and in what order) are the part
// a refactor breaks without breaking a render, and happy-dom cannot see a grid.

/** How many rows a month grid draws. Six, always — a fixed grid never reflows between months. */
export const MONTH_ROWS = 6
/** …which is 42 cells. */
export const MONTH_CELLS = MONTH_ROWS * 7

/**
 * The 42 days a month grid draws: six Monday-first weeks covering the month `anchor` falls in,
 * with the leading and trailing days of the neighbouring months included.
 *
 * SIX ROWS ALWAYS, even for a February that fits in four. A grid whose height depends on the
 * month makes every cell a different size as you page through the year, and the "+N more" line
 * would then appear and disappear for reasons that have nothing to do with the events.
 *
 * The mini-month in the left rail draws from this same function, which is what makes the rail
 * and the grid agree by construction rather than by two people writing the same loop twice.
 */
export function monthMatrix(anchor: ISODate): ISODate[] {
  const first = `${anchor.slice(0, 7)}-01` as ISODate
  const start = startOfWeek(first)
  return Array.from({ length: MONTH_CELLS }, (_, i) => addDays(start, i))
}

/** One line in a month cell: whichever of the three entities the calendar can put on a day. */
export type MonthEntry =
  | { kind: 'allDay'; id: string; event: CalendarAllDayEvent }
  | { kind: 'due'; id: string; item: CalendarDueItem }
  | { kind: 'block'; id: string; block: CalendarBlock }

/**
 * Everything one day of the month grid has to show, in the order it shows it.
 *
 * THE ORDER IS A HONESTY RULE, NOT A PREFERENCE. Things with NO TIME OF DAY come first, as a
 * group — Google's whole-day events, and the Canvas deadlines Canvas gave no time for (brief
 * rule 5). Everything with a real clock time follows, sorted by it. The alternative, sorting
 * everything by a minute, forces a minute onto the rows that do not have one, which is the
 * 11:59 PM invention rule 5 exists to forbid.
 *
 * SUGGESTIONS CANNOT REACH A MONTH CELL. `countedBlocks` is the same filter the day summary and
 * the header count use: an unaccepted proposal is not on the calendar for any purpose (rule 2),
 * and a month grid is the last place it should look like a plan.
 */
export function monthEntries(
  date: ISODate,
  blocks: readonly CalendarBlock[],
  dueItems: readonly CalendarDueItem[],
  allDayEvents: readonly CalendarAllDayEvent[],
): MonthEntry[] {
  const untimed: MonthEntry[] = [
    // A multi-day event is on every day it covers — the same rule the week's all-day row keeps.
    ...allDayEvents
      .filter((e) => e.date <= date && date <= e.endDate)
      .map((event) => ({ kind: 'allDay' as const, id: event.id, event })),
    ...dueItems
      .filter((d) => d.date === date && (!d.hasTime || d.dueMinutes === null))
      .map((item) => ({ kind: 'due' as const, id: item.id, item })),
  ]

  const timed: { at: number; entry: MonthEntry }[] = [
    ...countedBlocks(blocks)
      .filter((b) => b.date === date)
      .map((block) => ({ at: block.startMinutes, entry: { kind: 'block' as const, id: block.id, block } })),
    ...dueItems
      .filter((d) => d.date === date && d.hasTime && d.dueMinutes !== null)
      .map((item) => ({ at: item.dueMinutes!, entry: { kind: 'due' as const, id: item.id, item } })),
  ].sort((a, b) => a.at - b.at)

  return [...untimed, ...timed.map((t) => t.entry)]
}
